/**
 * PostHog analytics provider implementation.
 */

import type { AnalyticsProvider } from '../types/providers.js';
import type { AnalyticsEvent } from '../types/events.js';
import { PostHogManager } from '#~/posthog/index.js';
import { resolveEventGroups } from '../utils/groups-resolver.js';
import { groupIdentifyCache } from '../utils/group-identify-cache.js';
import { EVENT_NAMES } from '../constants/event-names.js';

// A boot burst can briefly exhaust posthog-js's client token bucket before these
// once-per-document lifecycle events reach its send boundary, silently dropping
// them client-side (the legacy trio was exempted for exactly this in 1761f04518).
// The canonical boot names keep the exemption that protected their retired legacy
// predecessors. The old constants remain in this set for compatibility with captured
// historical events, not because current senders use them. studio_load_succeeded and
// its historical loaded_studio name are both non-exempt because room mounting happens
// after the boot burst. Keep this set bounded; all other events retain rate-limit
// protection.
//
// The auto-link boot pair rides the SAME burst: attempt fires when boot auto-link
// begins, and success habitually lands within ~1s of boot_succeeded — exactly
// when the burst has just drained the token bucket — so a heavy boot ships
// boot_succeeded but silently drops the link success (surfaced by the
// auto-link-studio-list e2e receipt observing zero success sends at the boundary).
// Current senders emit bridge_link_attempt / bridge_link_succeeded. The retired
// auto_link_bridge_* constants stay in this set as historical classification,
// matching APP_BOOT_* above. posthog/manager.ts already classifies the canonical
// names as boot-once retained lifecycle events; this set must agree.
// Per rule: map-migration-targets-first (ai.knowledge/global) — the tuple moved
// and the exemption must move with it. The rest of the auto_link_bridge_* family
// (failure/timeout/error/no_devices/retry/disabled/manual_fallback) stays
// protected: only proven boot-burst casualties get in.
//
// screen_room_boot belongs here for the same measured reason. It is emitted on entry
// to /room/:roomId/boot, i.e. inside the boot burst, and it was being dropped before
// posthog-js ever captured it: the app logged the emit on every launch of CI run
// 30287767219 (mac 15/15, win 16/16) while staging received it on only 7 of 34
// launches, and a send-boundary receipt asserting it timed out with the event absent
// from the captured set entirely. The other three screens are NOT exempt —
// screen_room lands on 34/34 launches because it fires after the room route mounts,
// and the inspect / billing screens are user-driven, well clear of the burst. An
// exemption for a screen that can repeat per navigation would also remove the
// protection the limiter exists to provide.
//
// discovery_mismatch is the same casualty pattern outside the boot burst, and it
// was MEASURED, not predicted (HIL job 91320697969, 2026-08-01): one discovery
// pass used to emit ~14 discovery_device_found + ~14 device_discovery_device_found
// before the old sender retired. It then called emitDiscoveryMismatches
// (selector.tsx), so the found flood drains the bucket and every mismatch in
// the same pass is dropped
// client-side. That run's session dedupe came back holding five reconciled hosts
// — proof the app detected and emitted all five — while the send boundary
// observed ZERO discovery_mismatch and 14 of each sibling. The event is
// self-bounding (once per host per session via the sessionStorage dedupe in
// discovery-mismatch.ts), so exempting it cannot produce a flood: it is a
// strictly safer member of this set than the boot events. Without it the event
// is dead in production — detection runs, no row ever reaches PostHog.
// See ci-reports/mismatch-not-observed.md for the full evidence chain.
const RATE_LIMIT_EXEMPT_EVENTS: ReadonlySet<string> = new Set([
  EVENT_NAMES.APP_BOOT_STARTED,
  EVENT_NAMES.APP_BOOT_COMPLETED,
  EVENT_NAMES.APP_BOOT_FAILED,
  EVENT_NAMES.BOOT_STARTED,
  EVENT_NAMES.BOOT_SUCCEEDED,
  EVENT_NAMES.BOOT_FAILED,
  EVENT_NAMES.AUTO_LINK_BRIDGE_ATTEMPT,
  EVENT_NAMES.AUTO_LINK_BRIDGE_SUCCESS,
  EVENT_NAMES.BRIDGE_LINK_ATTEMPT,
  EVENT_NAMES.BRIDGE_LINK_SUCCEEDED,
  EVENT_NAMES.SCREEN_ROOM_BOOT,
  EVENT_NAMES.DISCOVERY_MISMATCH,
  // Count each accepted operator action, even inside a boot burst.
  EVENT_NAMES.ADD_POSITION_PRESET,
  EVENT_NAMES.RECALL_POSITION_PRESET
]);

export class PostHogProvider implements AnalyticsProvider {
  name = 'posthog';
  private manager = PostHogManager.getInstance();

  /**
   * Initialize the PostHog provider.
   *
   * Note: Actual initialization is handled by PostHogManager in the app root.
   * This just waits for PostHog to be ready.
   */
  async initialize(): Promise<void> {
    try {
      await this.manager.waitForReady(5000);
    } catch (error) {
      console.error('[PostHogProvider] Failed to wait for PostHog ready:', error);
    }
  }

  /**
   * Track an event with PostHog.
   *
   * @param event The event to track
   */
  async track(event: AnalyticsEvent): Promise<void> {
    const groups = resolveEventGroups(event.properties);

    this.manager.capture(
      event.name,
      {
        ...event.properties,
        ...(groups ? { $groups: groups } : {}),
        category: event.category,
        timestamp: event.timestamp || Date.now()
      },
      RATE_LIMIT_EXEMPT_EVENTS.has(event.name) ? { skip_client_rate_limiting: true } : undefined
    );
  }

  /**
   * Identify a user with PostHog.
   *
   * @param userId The user's ID
   * @param traits Optional user traits/properties
   */
  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    this.manager.identify(userId, traits);
  }

  /**
   * Reset the PostHog state.
   */
  async reset(): Promise<void> {
    this.manager.reset();
    groupIdentifyCache.clear();
  }

  /**
   * Check if PostHog is initialized.
   */
  isInitialized(): boolean {
    return this.manager.isReady();
  }
}
